// ============================================================
// FeedbackModal — beta bug/suggestion reporting
// ============================================================
// Auto-capture du contexte : page courante, URL, user agent, taille ecran.
// User remplit type + severity + titre + description.
// ============================================================

import { useState, useEffect } from 'react';
import { sb, API_URL } from '../utils/config';

const TYPES = [
  { id: 'bug',        label: '🐛 Bug',        help: 'Quelque chose ne marche pas' },
  { id: 'suggestion', label: '💡 Suggestion', help: 'Une idée pour améliorer' },
  { id: 'question',   label: '❓ Question',   help: 'Tu veux comprendre un truc' },
  { id: 'autre',      label: '💬 Autre',      help: 'Tout le reste' },
];

const SEVERITIES = [
  { id: 'bloquant',    label: '🔴 Bloquant',    help: "Je peux pas utiliser l'app" },
  { id: 'genant',      label: '🟠 Gênant',      help: 'Ça fonctionne mais c\'est chiant' },
  { id: 'cosmetique',  label: '🟡 Cosmétique',  help: 'Visuel/UX, pas critique' },
  { id: 'nice-to-have',label: '🟢 Nice-to-have',help: 'Si vous avez le temps' },
];

export default function FeedbackModal({ open, onClose, user, profile, contextData = {} }) {
  const [type, setType] = useState('bug');
  const [severity, setSeverity] = useState('genant');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (open) {
      setError('');
      setSuccess(false);
      // Reset form on open (keep user's previous type choice as default)
      setTitle('');
      setDescription('');
    }
  }, [open]);

  async function handleSubmit(e) {
    e?.preventDefault();
    if (!description.trim()) {
      setError('Décris le problème au minimum.');
      return;
    }
    if (!user?.id) {
      setError('Tu dois être connecté.');
      return;
    }

    setSubmitting(true);
    setError('');
    try {
      // Capture auto du contexte (browser, url, page)
      const autoContext = {
        ...contextData,
        captured_at: new Date().toISOString(),
        viewport: `${window.innerWidth}x${window.innerHeight}`,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      };

      const page = window.location.hash ? window.location.hash.replace('#', '') : window.location.pathname;

      const { data: inserted, error: insertError } = await sb.from('user_feedback').insert({
        user_id: user.id,
        profile_id: profile?.id || null,
        type,
        severity: type === 'bug' ? severity : null,
        title: title.trim() || null,
        description: description.trim(),
        page,
        url: window.location.href,
        user_agent: navigator.userAgent,
        screen_size: `${window.screen.width}x${window.screen.height}`,
        context_data: autoContext,
      }).select('id').single();

      if (insertError) {
        console.error('[FeedbackModal] insert error:', insertError);
        setError(`Oups, erreur d'envoi : ${insertError.message}`);
        return;
      }

      // Notif admin via backend (non-bloquant, best-effort)
      if (inserted?.id) {
        try {
          const { data: sessData } = await sb.auth.getSession();
          const token = sessData?.session?.access_token;
          if (token) {
            fetch(`${API_URL}/api/feedback/notify`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
              body: JSON.stringify({ feedback_id: inserted.id }),
            }).catch(() => {});
          }
        } catch { /* silent */ }
      }

      setSuccess(true);
      setTimeout(() => { onClose(); }, 1800);
    } catch (e) {
      console.error('[FeedbackModal] exception:', e);
      setError(e.message || 'Erreur inattendue');
    } finally {
      setSubmitting(false);
    }
  }

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[9500] bg-black/60 flex items-end md:items-center justify-center animate-fade" onClick={onClose}>
      <div className="w-full max-w-lg bg-white rounded-t-2xl md:rounded-2xl max-h-[90vh] overflow-y-auto animate-in"
        onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="sticky top-0 bg-white z-10 px-5 pt-5 pb-3 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-bold text-ink">Remonter un bug / une suggestion</h2>
              <p className="text-xs text-ink-faint mt-0.5">Merci de nous aider à améliorer Merlin 🧙</p>
            </div>
            <button onClick={onClose} className="text-ink-faint hover:text-ink text-xl">✕</button>
          </div>
        </div>

        {/* Success state */}
        {success ? (
          <div className="px-5 py-10 text-center">
            <div className="text-5xl mb-3">✨</div>
            <p className="text-lg font-bold text-green mb-1">Merci !</p>
            <p className="text-sm text-ink-muted">Ton retour est bien reçu. On revient vers toi si besoin.</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="px-5 py-4 space-y-4">

            {/* Type selector */}
            <div>
              <label className="text-xs font-bold text-ink block mb-2">Quel type de retour ?</label>
              <div className="grid grid-cols-2 gap-2">
                {TYPES.map(t => (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => setType(t.id)}
                    className={`px-3 py-2 rounded-xl text-left transition-all border ${type === t.id ? 'border-orange bg-orange/5 ring-2 ring-orange' : 'border-gray-200 hover:border-gray-300'}`}
                  >
                    <p className="text-sm font-bold text-ink">{t.label}</p>
                    <p className="text-[10px] text-ink-faint leading-tight mt-0.5">{t.help}</p>
                  </button>
                ))}
              </div>
            </div>

            {/* Severity selector (only for bugs) */}
            {type === 'bug' && (
              <div className="animate-in">
                <label className="text-xs font-bold text-ink block mb-2">C'est grave ?</label>
                <div className="grid grid-cols-2 gap-2">
                  {SEVERITIES.map(s => (
                    <button
                      key={s.id}
                      type="button"
                      onClick={() => setSeverity(s.id)}
                      className={`px-3 py-2 rounded-xl text-left transition-all border ${severity === s.id ? 'border-orange bg-orange/5 ring-2 ring-orange' : 'border-gray-200 hover:border-gray-300'}`}
                    >
                      <p className="text-xs font-bold text-ink">{s.label}</p>
                      <p className="text-[10px] text-ink-faint leading-tight mt-0.5">{s.help}</p>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Title (optional) */}
            <div>
              <label className="text-xs font-bold text-ink block mb-1">Titre <span className="text-ink-faint font-normal">(optionnel)</span></label>
              <input
                type="text"
                value={title}
                onChange={e => setTitle(e.target.value)}
                placeholder="Résume en 1 phrase (ex: Le bouton sauver ne marche pas)"
                className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm focus:border-orange outline-none"
                maxLength={120}
              />
            </div>

            {/* Description (required) */}
            <div>
              <label className="text-xs font-bold text-ink block mb-1">Description *</label>
              <textarea
                value={description}
                onChange={e => setDescription(e.target.value)}
                placeholder={type === 'bug' ?
                  "Décris ce qui se passe. Sois précis : ce que tu faisais, ce que tu attendais, ce qui s'est passé à la place." :
                  type === 'suggestion' ?
                  "Décris ton idée. Pourquoi tu la trouves utile ? Dans quel moment elle aurait changé ta vie ?" :
                  "Pose ta question ou décris ce que tu veux partager."
                }
                rows={5}
                className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm focus:border-orange outline-none resize-none"
                maxLength={2000}
                required
              />
              <p className="text-[10px] text-ink-faint mt-1">{description.length}/2000 caractères</p>
            </div>

            {/* Auto-captured context notice */}
            <div className="px-3 py-2 rounded-lg bg-blue-50 border border-blue-200 text-[10px] text-blue-800">
              ℹ On capture automatiquement : page courante ({window.location.hash || window.location.pathname}), navigateur, taille d'écran. Pas besoin de préciser.
            </div>

            {error && (
              <div className="px-3 py-2 rounded-lg bg-red-50 border border-red-200 text-xs text-red-700">
                {error}
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-2 justify-end pt-2">
              <button type="button" onClick={onClose} disabled={submitting}
                className="px-4 py-2 text-sm text-ink-muted hover:bg-gray-50 rounded-xl transition-all">
                Annuler
              </button>
              <button type="submit" disabled={submitting || !description.trim()}
                className="px-5 py-2 bg-orange text-white text-sm font-bold rounded-xl hover:bg-orange/90 transition-all disabled:opacity-30">
                {submitting ? 'Envoi...' : 'Envoyer mon retour'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
